import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormControlLabel, Switch, Autocomplete, TextField, Button, Box, Card } from '@mui/material';
import {
  fetchTicketureAllEventIDs,
  fetchTicketureByEventIDs,
  fetchTicketureByOpenDate,
  fetchTicketureDailyTotals
} from '../services/ticketure';
import { excludeCancelledEvents } from '../utils/excludeCancelledEvents';
import SkeletonLoader from './SkeletonLoader';
import Chart from 'react-apexcharts';
import dayjs from 'dayjs';

const Dashboard = () => {

  const [selectedEventIDs, setSelectedEventIDs] = useState([]);
  const [appliedEventIDs, setAppliedEventIDs] = useState([]);
  const [showCumulative, setShowCumulative] = useState(false);
  const [eventChartSeries, setEventChartSeries] = useState([]);
  const [totalChartSeries, setTotalChartSeries] = useState([]);
  const [eventData, setEventData] = useState([]);
  const totalsRangeRef = useRef(null);

  const dispatch = useDispatch();
  const state = useSelector((state) => state.ticketure);
  const companyName = useSelector((state) => state.company.company);
  const rawDailyTotals = state?.dailyTotals;
  const dailyTotals = useMemo(() => (Array.isArray(rawDailyTotals) ? rawDailyTotals : []), [rawDailyTotals]);
  const dailyTotalsLoading = state?.dailyTotalsLoading;
  const startDate = dayjs().subtract(1, 'day').startOf('week').format('YYYY-MM-DD');
  const endDate = dayjs().add(7, 'day').endOf('week').format('YYYY-MM-DD');

  const handlePasteEventIDs = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
  
    // Acceptable delimiters: comma, newline, tab, semicolon
    const rawIDs = pastedText
      .split(/[,;\n\t]+/)
      .map(id => id.trim())
      .filter(Boolean);
  
    // Validate against available event IDs
    const validEventIDs = rawIDs.filter(id => state?.allEventIds?.includes(id));
    const invalidEventIDs = rawIDs.filter(id => !state?.allEventIds?.includes(id));
  
    // Combine with existing selections, removing duplicates
    const combinedIDs = Array.from(new Set([...selectedEventIDs, ...validEventIDs]));
    setSelectedEventIDs(combinedIDs);
  
    if (invalidEventIDs.length > 0) {
      alert(`Some Event IDs were not recognized and were not added: \n${invalidEventIDs.join(', ')}`);
    }
  };

  // Fetch event data based on companyName
  useEffect(() => {
    setEventChartSeries([]);
    setTotalChartSeries([]);
    setEventData([]);
    dispatch(fetchTicketureAllEventIDs());
    dispatch(fetchTicketureByOpenDate({ startDate, endDate }));
    totalsRangeRef.current = null;
  }, [dispatch, startDate, endDate, companyName]);

  // Set applied event IDs
  useEffect(() => {
    if (state?.defaultEventIDs && state?.defaultEventIDs.length > 0) {
      const uniqueEventIDs = Array.from(new Set(state?.defaultEventIDs.map(event => event.event_id)));
      const validEventIDs = excludeCancelledEvents(uniqueEventIDs);
      setAppliedEventIDs(validEventIDs);
    }
  }, [state?.defaultEventIDs]);

  const handleToggleChange = () => {
    setShowCumulative(!showCumulative);
  };

  const formatCurrency = useCallback((value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
      return '$0';
    }
    return numeric.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }, []);

  const getNumericalValue = useCallback((item, keys) => {
    for (const key of keys) {
      const value = item?.[key];
      if (value !== undefined && value !== null) {
        const numericValue = Number(value);
        if (!Number.isNaN(numericValue)) {
          return numericValue;
        }
      }
    }
    return 0;
  }, []);

  const getDaysOutValue = useCallback((item) => {
    const value = item?.days_out ?? item?.daysOut ?? item?.days_until_start;
    if (value === undefined || value === null) {
      return null;
    }
    const numeric = Number(value);
    return Number.isNaN(numeric) ? null : numeric;
  }, []);

  const getSaleDateValue = useCallback((item) => item?.sale_date ?? item?.date ?? item?.sales_date ?? null, []);

  const buildEventSeries = useCallback((data) => {
    if (!Array.isArray(data) || data.length === 0) {
      return [];
    }

    const groupedData = data.reduce((acc, item) => {
      const eventId = item?.event_id;
      if (!eventId) {
        return acc;
      }
      if (!acc[eventId]) {
        acc[eventId] = new Map();
      }

      const daysOut = getDaysOutValue(item);
      const saleDate = getSaleDateValue(item);
      if (daysOut === null || !saleDate) {
        return acc;
      }

      const dailyTickets = getNumericalValue(item, ['daily_ticket_count', 'daily_ticket_total', 'ticket_count']);
      const dailyOrders = getNumericalValue(item, ['daily_orders', 'orders']);
      const dailyMerch = getNumericalValue(item, ['daily_merch_count', 'merch']);

      const existing = acc[eventId].get(daysOut) || {
        saleDate,
        dailyTickets: 0,
        dailyOrders: 0,
        dailyMerch: 0,
      };

      existing.dailyTickets += dailyTickets;
      existing.dailyOrders += dailyOrders;
      existing.dailyMerch += dailyMerch;

      acc[eventId].set(daysOut, existing);
      return acc;
    }, {});

    return Object.keys(groupedData).map((eventId) => {
      const sortedEntries = Array.from(groupedData[eventId].entries())
        .map(([daysOut, values]) => ({ daysOut, ...values }))
        .sort((a, b) => a.daysOut - b.daysOut);

      let runningTickets = 0;
      let runningOrders = 0;
      let runningMerch = 0;

      const cumulativeSnapshots = new Array(sortedEntries.length);
      for (let i = sortedEntries.length - 1; i >= 0; i -= 1) {
        const entry = sortedEntries[i];
        runningTickets += entry.dailyTickets;
        runningOrders += entry.dailyOrders;
        runningMerch += entry.dailyMerch;
        cumulativeSnapshots[i] = {
          tickets: runningTickets,
          orders: runningOrders,
          merch: runningMerch,
        };
      }

      const dataPoints = sortedEntries.map((entry, idx) => ({
        x: entry.daysOut,
        sale_date: entry.saleDate,
        y: showCumulative ? cumulativeSnapshots[idx].tickets : entry.dailyTickets,
        ordersValue: showCumulative ? cumulativeSnapshots[idx].orders : entry.dailyOrders,
        merchValue: showCumulative ? cumulativeSnapshots[idx].merch : entry.dailyMerch,
      }));

      return {
        name: eventId,
        data: dataPoints,
      };
    });
  }, [getDaysOutValue, getNumericalValue, getSaleDateValue, showCumulative]);

  const buildTotalsSeries = useCallback((data) => {
    if (!Array.isArray(data) || data.length === 0) {
      return [];
    }

    const normalized = data
      .map(item => {
        const saleDate = item?.sale_date ?? item?.saleDate ?? getSaleDateValue(item);
        const tickets = getNumericalValue(item, ['total_tickets', 'totalTickets', 'ticket_total']);
        const revenue = getNumericalValue(item, ['total_revenue', 'totalRevenue', 'revenue_total']);
        if (!saleDate) {
          return null;
        }
        return { saleDate, tickets, revenue };
      })
      .filter(Boolean);

    if (normalized.length === 0) {
      return [];
    }

    normalized.sort((a, b) => dayjs(a.saleDate).valueOf() - dayjs(b.saleDate).valueOf());

    let runningTickets = 0;
    let runningRevenue = 0;

    const ticketPoints = [];
    const revenuePoints = [];

    normalized.forEach(item => {
      runningTickets += item.tickets;
      runningRevenue += item.revenue;

      ticketPoints.push({
        x: dayjs(item.saleDate).valueOf(),
        sale_date: item.saleDate,
        y: showCumulative ? runningTickets : item.tickets,
        tickets: item.tickets,
        cumulativeTickets: runningTickets,
        revenue: showCumulative ? runningRevenue : item.revenue,
        cumulativeRevenue: runningRevenue,
      });

      revenuePoints.push({
        x: dayjs(item.saleDate).valueOf(),
        sale_date: item.saleDate,
        y: showCumulative ? runningRevenue : item.revenue,
        tickets: showCumulative ? runningTickets : item.tickets,
        revenue: showCumulative ? runningRevenue : item.revenue,
      });
    });

    return [
      {
        name: 'Tickets',
        type: 'line',
        data: ticketPoints,
      },
      {
        name: 'Revenue',
        type: 'line',
        data: revenuePoints,
      },
    ];
  }, [getNumericalValue, getSaleDateValue, showCumulative]);

  useEffect(() => {
    setEventChartSeries(buildEventSeries(eventData));
  }, [eventData, buildEventSeries]);

  useEffect(() => {
    setTotalChartSeries(buildTotalsSeries(dailyTotals));
  }, [dailyTotals, buildTotalsSeries]);

  useEffect(() => {
    if (appliedEventIDs.length > 0) {
      const fetchAllEventData = async () => {
        const allEventData = await Promise.all(appliedEventIDs.map(eventID => dispatch(fetchTicketureByEventIDs(eventID))));
        const rawData = allEventData.flatMap((response) => response.payload);
        setEventData(rawData);
      };
      fetchAllEventData();
    }
  }, [dispatch, appliedEventIDs, companyName]);

  useEffect(() => {
    if (appliedEventIDs.length === 0) {
      setEventData([]);
    }
  }, [appliedEventIDs]);

  useEffect(() => {
    const saleDates = (eventData || [])
      .map(item => getSaleDateValue(item))
      .filter(Boolean);

    let rangeStart = startDate;
    let rangeEnd = endDate;

    if (saleDates.length > 0) {
      const bounds = saleDates.reduce(
        (acc, dateString) => {
          const parsed = dayjs(dateString);
          if (!parsed.isValid()) {
            return acc;
          }
          if (!acc.min || parsed.isBefore(acc.min)) {
            acc.min = parsed;
          }
          if (!acc.max || parsed.isAfter(acc.max)) {
            acc.max = parsed;
          }
          return acc;
        },
        { min: null, max: null }
      );

      if (bounds.min) {
        rangeStart = bounds.min.format('YYYY-MM-DD');
      }
      if (bounds.max) {
        rangeEnd = bounds.max.format('YYYY-MM-DD');
      }
    }

    const rangeKey = `${companyName}_${rangeStart}_${rangeEnd}`;

    if (totalsRangeRef.current === rangeKey) {
      return;
    }

    totalsRangeRef.current = rangeKey;
    dispatch(fetchTicketureDailyTotals({ startDate: rangeStart, endDate: rangeEnd }))
      .unwrap()
      .catch(() => {
        totalsRangeRef.current = null;
      });
  }, [dispatch, eventData, companyName, startDate, endDate, getSaleDateValue]);

  const handleApply = () => {
    setAppliedEventIDs([...selectedEventIDs]);
  };

  const chartOptions = {
    chart: {
      id: 'mainChart',
      type: 'line',
      toolbar: { show: true },
      zoom: { enabled: false },
    },
    markers: { size: 3, strokeWidth: 1 },
    stroke: { width: 3, curve: 'smooth', lineCap: 'round' },
    xaxis: {
      title: { text: 'Days Out' },
      type: 'numeric',
      labels: { formatter: (value) => Math.floor(value) },
      min: -2,
      max: 60,
    },
    yaxis: {
      title: { text: showCumulative ? 'Cumulative Ticket Count' : 'Daily Ticket Count' },
      min: 0,
    },
    title: { text: 'Ticket Sales Over Time', align: 'left' },
    legend: { position: 'top', horizontalAlign: 'left' },
    tooltip: {
      enabled: true,
      custom: function({ series, seriesIndex, dataPointIndex, w }) {
        const dataPoint = w.config.series[seriesIndex].data[dataPointIndex];
        const daysOut = dataPoint.x;
        const ticketCount = dataPoint.y;
        const merchCount = dataPoint.merchValue;
        const saleDate = dataPoint.sale_date;
        const orderCount = dataPoint.ordersValue;
  
        return `
          <div style="padding: 10px; border: 1px solid #ccc; background: white;">
            <strong>Event: ${w.config.series[seriesIndex].name}</strong><br />
            Days Out: ${daysOut}<br />
            Sale Date: ${saleDate}<br />
            Ticket Count: ${ticketCount}<br />
            Add Ons: ${merchCount}<br />
            Orders: ${orderCount}
          </div>
        `;
      },
    },
  };

  const totalChartOptions = {
    chart: {
      id: 'totalsChart',
      type: 'line',
      toolbar: { show: true },
      zoom: { enabled: false },
    },
    stroke: { width: [3, 3], curve: 'smooth', lineCap: 'round' },
    markers: { size: [3, 3], strokeWidth: 1 },
    colors: ['#111827', '#2563EB'],
    xaxis: {
      title: { text: 'Sale Date' },
      type: 'datetime',
      labels: { datetimeUTC: false },
    },
    yaxis: [
      {
        seriesName: 'Tickets',
        title: { text: showCumulative ? 'Cumulative Tickets' : 'Daily Tickets' },
        min: 0,
        labels: {
          formatter: (value) => Number(value ?? 0).toLocaleString(),
        },
      },
      {
        seriesName: 'Revenue',
        opposite: true,
        title: { text: showCumulative ? 'Cumulative Revenue ($)' : 'Daily Revenue ($)' },
        min: 0,
        labels: {
          formatter: (value) => formatCurrency(value),
        },
      },
    ],
    tooltip: {
      shared: true,
      intersect: false,
      x: { format: 'yyyy-MM-dd' },
      y: {
        formatter: (value, { seriesIndex }) => {
          if (seriesIndex === 0) {
            return `${Number(value).toLocaleString()} tickets`;
          }
          return formatCurrency(value);
        },
      },
    },
    title: { text: 'All Events Ticket Sales & Revenue', align: 'left' },
    legend: { position: 'top', horizontalAlign: 'left' },
  };

  const brushChartOptions = {
    chart: {
      id: 'brushChart',
      height: 130,
      type: 'area',
      brush: {
        target: 'mainChart',
        enabled: true,
      },
      selection: {
        enabled: true,
        xaxis: {
          min: -2,
          max: 60,
        },
      },
    },
    xaxis: {
      type: 'numeric',
      labels: { formatter: (value) => Math.floor(value) },
    },
    yaxis: {
      tickAmount: 2,
    },
  };

  const eventChartHasData = eventChartSeries.some(series => Array.isArray(series.data) && series.data.length > 0);
  const showEventSkeleton = state?.loading && !eventChartHasData;
  const totalsChartHasData = totalChartSeries.some(series => Array.isArray(series.data) && series.data.length > 0);
  const showTotalsSkeleton = dailyTotalsLoading && !totalsChartHasData;

  return (
    <div style={{ margin: '10px 30px', background: '#F1F5F9' }}>
      <Card>
        <Box sx={{ p: 3, pb: 1 }} dir="ltr">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div style={{ display: 'flex', gap: '12px', padding: '10px' }}>
              <FormControlLabel
                control={<Switch checked={showCumulative} onChange={handleToggleChange} />}
                label="Show Cumulative Sales"
              />
            </div>
            <Autocomplete
              multiple
              options={state?.allEventIds || []}
              getOptionLabel={option => option}
              value={selectedEventIDs}
              onChange={(event, newValue) => setSelectedEventIDs(newValue)}
              renderInput={params => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Select Event IDs"
                  onPaste={handlePasteEventIDs}
                />
              )}
              style={{ width: '70%' }}
            />
            <Button variant="contained" color="primary" onClick={handleApply}>Apply</Button>
          </div>

          {showEventSkeleton ? (
            <SkeletonLoader loading={showEventSkeleton} width="100%" height={450} />
          ) : eventChartHasData ? (
            <>
              <Chart options={chartOptions} series={eventChartSeries} type="line" height={450} style={{ marginTop: 20 }} />
              <Chart options={brushChartOptions} series={eventChartSeries} type="area" height={130} />
            </>
          ) : (
            <p>No event sales data available for the current selection.</p>
          )}
        </Box>
        <Box sx={{ p: 3, pt: 0 }} dir="ltr">
          {showTotalsSkeleton ? (
            <SkeletonLoader loading={showTotalsSkeleton} width="100%" height={300} />
          ) : totalsChartHasData ? (
            <Chart options={totalChartOptions} series={totalChartSeries} type="line" height={300} />
          ) : (
            <p>No aggregate ticket sales data available.</p>
          )}
        </Box>
      </Card>
    </div>
  );
};

export default Dashboard;
